/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CycleOverViewList.module.css';
import { Cycle, CycleOverviewPermission, getcycleList } from './CycleOverviewListHelper';
import { Button, Typography, Dialog, DialogContent, TextField, Grid, InputAdornment } from '@mui/material';
import { ReactComponent as SearchIcon } from '../../assets/SVG/Search-Icon.svg';
import CreateNewCycle from './CreateNewCycle';
import TerminateCycleConfirmation from './TerminateCycleConfirmation';
import CycleDetails from './CycleDetails';
import { useLoader } from '../loaderProvider/loader';
import { useAlert } from '../alertProvider';
import UpdateDisabledSharpIcon from '@mui/icons-material/UpdateDisabledSharp';
import { usePatientContext } from '../SubPatinetProvider';
import { usePatientContext as MainPatientContext } from '../patinetProvider';
import { Avatar, Tooltip } from '@mui/material'; 
import AvatarGroup from '@mui/material/AvatarGroup';
import { useLocation, useParams } from 'react-router-dom';
import { useCycle } from '../cycleContext/CycleContext';
import { convertUnderscoreToTitle, getStoredPermissions } from '../../utils/commonUtils';
import { patientDetailsObservable } from '../../services/sharedService';
import { formatDate } from '../../utils/dateUtils';



interface OverViewListProps {
  gender?: 'MALE' | 'FEMALE';
}

const CycleOverviewList = ({ gender }: OverViewListProps) => {
  const { t } = useTranslation('cycleOverview_config');
  const [isCreateCycleDialogOpen, setCreateCycleDialogOpen] = React.useState(false);
  const [isTerminateCycleDialogOpen, setTerminateCycleDialogOpen] = React.useState(false);
  const [isCycleDetailsVisible, setCycleDetailsVisible] = useState(false);
  const [selectedIdTerminate, setSelectedIdTerminate] = useState<string | null>(null);
  const { showLoader, hideLoader } = useLoader();
  const { addAlert } = useAlert();
  const { patientId } = usePatientContext();
  const { MainpatientDetails } = MainPatientContext();
  const [cyclesData, setCyclesData] = useState<Cycle[]>([]); 
  const [filteredCycles, setFilteredCycles] = useState<Cycle[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<string | null>(null);
  const location = useLocation();
  const { setCycleId } = useCycle();
  const { id } = useParams();
  const [patientDetails, setpatientDetails] = useState<any >(null);
  const allowedCycleOverviewPermissions: CycleOverviewPermission | { type: [] } = React.useMemo(() => {
    const permissionsData = getStoredPermissions();
    const clinicalModule = permissionsData?.modules?.find(
      (m: any) => m.name === 'Clinical' && m.featureEnabled
    );
    if (!clinicalModule) return { type: [] };
    const TreatmentModule = clinicalModule.submodules.find(
      (sm: any) => sm.name === 'Cycle Overview'
    );
    return TreatmentModule || { type: [] };
  }, []);
    
  const hasAccess = (type: string) => {
    if (!allowedCycleOverviewPermissions || !Array.isArray(allowedCycleOverviewPermissions.type)) return false;
    const cycleTypeObj = allowedCycleOverviewPermissions.type.find((t) => t.name === 'Cycle Overview');
    if (!cycleTypeObj || !Array.isArray(cycleTypeObj.permissions)) return false;
    return cycleTypeObj.permissions.some((perm) => perm.access === type);
  };

  const canAdd = hasAccess('add');
  const canEditOrView = hasAccess('edit') || hasAccess('view');
  const canEdit = hasAccess('edit');


  useEffect(() => {
    const subscription = patientDetailsObservable.subscribe((value) => {
      setpatientDetails(value);
    });
  
    return () => subscription.unsubscribe();
  }, []);

  const handleOpenDialog = () => {
    setCreateCycleDialogOpen(true);
  };


  useEffect(()=>{
    if (location.state?.cycleId) {
      setCycleDetailsVisible(true);
      setSelectedCycle(location.state.cycleId);
    } else if (location.search) {
      const queryParams = new URLSearchParams(location.search);
      const cycleIdFromQuery = queryParams.get('cycleId');
      if (cycleIdFromQuery) {
        setCycleDetailsVisible(true);
        setSelectedCycle(cycleIdFromQuery);
      }
    }
    
  },[location]);
  const handleCloseDialog = () => {
    setCreateCycleDialogOpen(false);
  };

  const handleTerminateCloseDialog = () => {
    setTerminateCycleDialogOpen(false);
  };

   
  const handleRowClick = (cycle: Cycle) => {
    setCycleDetailsVisible(true);
    setSelectedCycle(cycle.id);
    if (!['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'].includes(patientDetails?.type)) {
      setCycleId(Number(cycle.id));
    }
  };
  
  const handleCycleListClick = () => {
    setCycleDetailsVisible(false);
  };

  useEffect(() => {

    return () => {
      setCycleId(null); 
    };
  }, [setCycleId]);

  useEffect(() => {
    showLoader();

    const getPatientId =
    ['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'].includes(patientDetails?.patient_type)
      ? patientId
      : ['PATIENT_COUPLE', 'PATIENT_SINGLE'].includes(patientDetails?.patient_type)
        ? MainpatientDetails?.id
        : id;
    
    getcycleList(String(getPatientId))
      .then((data: Cycle[]) => { 
        setTimeout(() => {
          setCyclesData(data);  
          setFilteredCycles(data); 
          hideLoader();
        }, 200);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return; // Ignore abort errors
        hideLoader();
        if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const errorMessage = error.response.data.errors.map((err: { detail: string }) => err.detail).join(', ');
          if (error.response.status !== 404) {
            addAlert(errorMessage, 'error');
          }
          setCyclesData([]); 
        } else {
          // eslint-disable-next-line no-console
          console.error('Error fetching cycle data:', error);
          // addAlert('Error fetching cycle data', 'error');
        }
      });

  }, [showLoader, hideLoader, addAlert, id, isCreateCycleDialogOpen, isTerminateCycleDialogOpen, patientDetails, patientId,MainpatientDetails]);



  useEffect(() => {
    if (typeof searchTerm === 'string' && searchTerm.trim() === '') {
      setFilteredCycles(cyclesData);  
    } else {
      const filtered = cyclesData.filter((cycle) =>
        (typeof cycle.cycle_number === 'string' && cycle.cycle_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (typeof cycle.procedure?.procedures === 'string' && cycle.procedure?.procedures.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCycles(filtered);  
    }
  }, [searchTerm, cyclesData]);


  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (typeof value === 'string' && value.trim() === '') {
      setFilteredCycles(cyclesData);  
    }
  };
  
  return (
    <div style={{ padding: '1.5rem' }}>
      {isCycleDetailsVisible && selectedCycle !== null ? (
        <CycleDetails 
          handleCycleListClick={handleCycleListClick} 
          cycle={selectedCycle} 
          gender={gender}
        />
      ) : (
        <>


          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={6} xl={6}>
              <div>
                <Typography variant="h6" className={styles.CycleList}>
                  {t('CycleList')}
                </Typography>
              </div>
            </Grid>
              
            <Grid item xs={12} sm={6} md={6} xl={6}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                {canEditOrView &&(
                
                  <TextField
                    id="outlined-basic"
                    label={t('SearchByCycle')}
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{
                      width: '500px',
                      marginRight: '16px',
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                      inputLabel: {
                        shrink: searchFocused || searchTerm.length > 0,
                      },
                    }}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    sx={{
                      width: '100%',
                      '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                        transform: 'translate(50px, 16px) scale(1)',
                      },
                    }}
                  />
                )}
                {canAdd && (() => {
                  const hasActiveCycle = cyclesData && cyclesData.some((cycle) => cycle.status === 'ACTIVE');
                  const isLinkedPartner = MainpatientDetails?.id != null && patientDetails?.id != null && MainpatientDetails.id !== patientDetails.id && ['PATIENT_COUPLE', 'PATIENT_SINGLE'].includes(patientDetails?.patient_type);
                  const isDisabled = hasActiveCycle || isLinkedPartner;
                  const tooltipMessage = hasActiveCycle
                    ? t('ActiveCycleExists')
                    : isLinkedPartner
                      ? t('CannotCreateCycleForLinkedPartner')
                      : '';
                  return (
                    <Tooltip title={isDisabled ? tooltipMessage : ''} arrow>
                      <span style={isDisabled ? { cursor: 'not-allowed' } : undefined}>
                        <Button
                          variant="contained"
                          color="primary"
                          className={styles.AddNewCycle}
                          onClick={handleOpenDialog}
                          disabled={isDisabled}
                          sx={isDisabled ? { pointerEvents: 'none' } : undefined}
                        >
                          <span className={styles.AddNewCycleText}>{t('CreateNewCycle')}</span>
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()}
              </div>
            </Grid>
          </Grid>

          {canEditOrView&&(
            <div className="mt-4">
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table className={styles.custom_table}>
                  <thead className={styles.custom_table_head}>
                    <tr>
                      {(canEdit || canAdd) &&(

                        <th className={styles.custom_table_cell} style={{width:'4.5rem'}}></th>
                      )}
                      <th className={styles.custom_table_cell}>{t('CycleStatus')}</th>
                      <th className={styles.custom_table_cell}>{t('CycleNumber')}</th>
                      <th className={styles.custom_table_cell}>{t('LMP')}</th>
                      <th className={styles.custom_table_cell}>{t('CycleName')}</th>
                      <th className={styles.custom_table_cell}>{t('MedicationProtocol')}</th>
                      <th className={styles.custom_table_cell}>{t('Indication')}</th>
                      <th className={styles.custom_table_cell}>{t('OocyteSource')}</th>
                      <th className={styles.custom_table_cell}>{t('SpermSource')}</th>
                      <th className={styles.custom_table_cell}>{t('Follicules')}</th>
                      <th className={styles.custom_table_cell}>{t('OPU')}</th>
                      <th className={styles.custom_table_cell}>{t('Oocyte')}</th>
                      <th className={styles.custom_table_cell}>{t('Fertilized')}</th>
                      <th className={styles.custom_table_cell}>{t('Cleaved')}</th>
                      <th className={styles.custom_table_cell}>{t('Cryo')}</th>
                      <th className={styles.custom_table_cell}>{t('EmbryoTransfer')}</th>
                      <th className={styles.custom_table_cell}>{t('BHCGUPT')}</th>
                      <th className={styles.custom_table_cell}>{t('PregnancyConfirmation')}</th>
                      <th className={styles.custom_table_cell}>{t('RemainingEmbryos')}</th>
                      <th className={styles.custom_table_cell}>{t('CancelRemark')}</th>
                    </tr>
                  </thead>
                  <tbody className={styles.custom_table_body}>
                    {filteredCycles && filteredCycles.length > 0 ? (                  
                      filteredCycles.map((cycle) => (
                        <tr
                          key={cycle.id}
                          className={styles.custom_table_row}
                          onClick={() => handleRowClick(cycle)}
                        >
                          {(canEdit || canAdd) &&(
                          
                            <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`} style={{ width: '4.5rem' }}>
                              {cycle.status === 'ACTIVE' && (
                                <Tooltip title={t('Terminate Cycle')} arrow>
                                  <UpdateDisabledSharpIcon
                                    onClick={(event: { stopPropagation: () => void }) => {
                                      event.stopPropagation();
                                      setSelectedIdTerminate(cycle.id);
                                      setTerminateCycleDialogOpen(true);
                                    }}
                                    style={{ cursor: 'pointer' }} // Makes it clear that it's clickable
                                  />
                                </Tooltip>
                              )}
                            </td>
                          )}

                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            <span
                              className={`${styles.status_icon} ${
                                cycle.status === 'ACTIVE' ? styles.activeStatus : styles.inactiveStatus
                              }`}
                            >
                              {cycle.status === 'ACTIVE' ? t('Active') : t('Inactive')}
                            </span>
                          </td>
  
                          <td className={`${styles.custom_table_cell} ${styles.cycle_code_cell}`}>
                            {cycle.cycle_number || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {formatDate(cycle.lmp_date) || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.procedure?.procedures_label || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle?.medication_protocol_label || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {convertUnderscoreToTitle(cycle.procedure?.indication_label) || '-'}
                          </td>

                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.procedure?.donor_info?.oocyte_source?.length ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <AvatarGroup max={8}>
                                  {cycle.procedure?.donor_info?.oocyte_source?.map((source: { first_name: string; last_name: string; medical_record_no: string; photo: string | null }, index: number | null | undefined) => (
                                    <Tooltip key={index} title={`${source.first_name || ''} ${source.last_name || t('Self')} ${!source.last_name ? '' : ` , MRN : ${source.medical_record_no || '-'}`}`} arrow>
                                      <Avatar 
                                        alt={`${source.first_name} ${source.last_name}`} 
                                        src={source.photo || undefined} 
                                      >
                                        {!source.photo && 
                `${source.first_name?.charAt(0) || ''}${source.last_name?.charAt(0) || ''}`}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                </AvatarGroup>
                              </div>
                            ) : null}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.procedure?.donor_info?.sperm_source?.length ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <AvatarGroup max={8}>
                                  {cycle.procedure?.donor_info?.sperm_source?.map((source: { first_name: string; last_name: string; medical_record_no: string; photo: string | null }, index: number | null | undefined) => (
                                    <Tooltip key={index} title={`${source.first_name || ''} ${source.last_name || t('Partner')} ${!source.last_name ? '' : ` , MRN : ${source.medical_record_no || '-'}`}`} arrow>
                                      <Avatar 
                                        alt={`${source.first_name} ${source.last_name}`} 
                                        src={source.photo || undefined}
                                      >
                                        {!source.photo && 
                `${source.first_name?.charAt(0) || ''}${source.last_name?.charAt(0) || ''}`}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                </AvatarGroup>
                              </div>
                            ) : null}
                          </td>

                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {
                              (cycle.optimum_follicles?.left_follicles ?? 0) + (cycle.optimum_follicles?.right_follicles ?? 0) || '-'
                            }
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.opu_status === '-' ? (
                              '-'
                            ) : cycle.opu_status ? (
                              <div className={styles.statusBadgeSuccess}>
                                {t('Completed')}
                              </div>
                            ) : (
                              <div className={styles.statusBadgePending}>
                                {t('Pending')}
                              </div>
                            )}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.oocyte_count || '-'}
                          </td>


                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.fertilized_count || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.cleaved_count || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.total_embryooocyte_count || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div className={`${styles.squareBox} ${styles.greenBox}`}></div>
                              <span style={{ marginLeft: '0.5rem' }}>
                                {cycle.fresh_transfer_count !== null && cycle.fresh_transfer_count !== undefined
                                  ? cycle.fresh_transfer_count
                                  : '-'}
                              </span>




                              <div
                                className={`${styles.squareBox} ${styles.secondBox}`}
                                style={{ marginLeft: '1rem' }}
                              ></div>
                              <span style={{ marginLeft: '0.5rem' }}>
                                {cycle.frozen_transfer_count !== null && cycle.frozen_transfer_count !== undefined
                                  ? cycle.frozen_transfer_count
                                  : '-'}
                              </span>
                            </div>
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.outcome?.outcome_details[0].bhcg || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.outcome?.birth_details && cycle.outcome.birth_details.length > 0 ? (
                              convertUnderscoreToTitle(cycle.outcome.birth_details[0].outcome_type_label)
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {cycle.remaining_embryos || '-'}
                          </td>
                          <td className={`${styles.custom_table_cell} ${styles.tb_main_text}`}>
                            {convertUnderscoreToTitle(cycle.cancel_reason_label) || '-'}
                          </td>

                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={19} className={styles.noDataFound}>
                          {t('NoDataFound')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

  
              <div className="mt-4">
                <div className={styles.boxWrapper}>
                  <div className={`${styles.box} ${styles.box1} ${styles.custom_table_cell}`}></div>
                  <span className={styles.customtext}>{t('FreshEmbryo')}</span>
                  <div className={`${styles.box} ${styles.box2} ${styles.custom_table_cell}`}></div>
                  <span className={styles.customtext}>{t('FrozenEmbryo')}</span>
                </div>
              </div>
            </div>
          )}



        </>
      )}
 
  


      {/* Dialog to show CreateNewCycle component */}
      <Dialog
        open={isCreateCycleDialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          style: {
            borderRadius: '20px',
            width: '60rem',
            maxWidth: '70vw',
            padding: '1rem',
          },
        }}
        aria-labelledby="create-cycle-dialog-title"
      >
        <DialogContent
          className="p-0"
          style={{
            paddingLeft: '2rem',
            paddingRight: '2rem',
          }}
        >
          <CreateNewCycle onClose={handleCloseDialog} />
        </DialogContent>
      </Dialog>

      {/* Dialog to show Terminate Cycle component */}
      <Dialog
        open={isTerminateCycleDialogOpen}
        onClose={handleTerminateCloseDialog}
        PaperProps={{
          style: {
            borderRadius: '20px',
            width: '60rem',
            maxWidth: '70vw',
            padding: '1rem',
          },
        }}
        aria-labelledby="terminate-cycle-dialog-title"
      >
        <DialogContent
          className="p-0"
          style={{
            paddingLeft: '2rem',
            paddingRight: '2rem',
          }}
        >
          <TerminateCycleConfirmation onClose={handleTerminateCloseDialog} cycleId={selectedIdTerminate} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CycleOverviewList;
